import { useEffect, useState } from 'react';
import { DT, DS2 } from '../../theme/designTokens';
import { useFriends } from '../../context/FriendContext';
import { apiClient } from '../../network/ApiClient';
import { showSocialToast } from './SocialToastManager';
import { PresenceDot } from './PresenceDot';
import { CountryFlag } from '../ui/CountryFlag';
import { DS2Modal } from '../ui/DS2Modal';
import type { FriendListItem } from '../../types/social';
import { useTranslation } from '../../i18n/react';
import { renderUiMessage, uiFromError, type UiMessage } from '../../i18n/uiMessage';

interface Props {
  sessionId:     number;
  /** User IDs of players already seated, so we disable their invite button.
   *  P0 privacy: IDs, never emails (lobby payloads carry no email). */
  seatedUserIds: number[];
  onClose:       () => void;
}

/** Dark-on-light text colors for the canonical light-gray modal body.
 *  Alphas chosen for WCAG AA on #D9D9D9: 0.70 ≈ 6.5:1, 0.60 ≈ 4.7:1. */
const INK        = DS2.modal.bodyText;
const INK_MUTED  = 'rgba(11,11,11,0.70)';
const INK_FAINT  = 'rgba(11,11,11,0.60)';

/**
 * Friend-picker dialog for sending a game invitation from the lobby.
 * Renders through the canonical DS2Modal shell (blurred backdrop, black
 * header, light body) with a custom scrolling friend-list body; invitation
 * logic and per-row states are unchanged from the pre-migration component.
 */
export function InviteToGameModal({ sessionId, seatedUserIds, onClose }: Props) {
  const { friends, outgoingInvitations } = useFriends();
  const { t } = useTranslation('play');
  const [sentIds, setSentIds] = useState<Set<number>>(new Set());
  const [busyId,  setBusyId]  = useState<number | null>(null);
  const [error,   setError]   = useState<UiMessage | null>(null);

  // Pre-mark friends who already have a pending outgoing invitation for this session.
  useEffect(() => {
    const alreadySent = new Set(
      outgoingInvitations
        .filter(inv => inv.session.id === sessionId)
        .map(inv => inv.to_user.id)
    );
    setSentIds(alreadySent);
  }, [outgoingInvitations, sessionId]);

  async function handleInvite(friend: FriendListItem) {
    if (busyId !== null) return;
    setError(null);
    setBusyId(friend.friend.id);
    try {
      await apiClient.sendInvitation(friend.friend.id, sessionId);
      setSentIds(prev => new Set([...prev, friend.friend.id]));
      showSocialToast(t('invite.sent', { name: friend.friend.display_name }));
    } catch (e) {
      setError(uiFromError(e, 'invite.error'));
    } finally {
      setBusyId(null);
    }
  }

  const seatedIdSet = new Set(seatedUserIds);

  return (
    <DS2Modal
      open
      title={t('invite.title')}
      onDismiss={onClose}
      showClose
      maxWidth={480}
      fillBody
    >
      <div style={{ display: 'flex', flexDirection: 'column', minHeight: 0, maxHeight: 'min(440px, 60dvh)' }}>
        {/* Friend list — scrollable */}
        <div style={{ overflowY: 'auto', flex: 1, padding: '8px 0' }}>
          {friends.length === 0 ? (
            <div style={{ padding: '40px 20px', textAlign: 'center' }}>
              <p style={{ margin: 0, fontSize: 14, fontFamily: DS2.font.body, color: INK_MUTED }}>
                {t('invite.empty')}
              </p>
              <p style={{ margin: '6px 0 0', fontSize: 12, fontFamily: DS2.font.body, color: INK_FAINT }}>
                {t('invite.emptyHint')}
              </p>
            </div>
          ) : (
            friends.map(f => {
              const alreadySent  = sentIds.has(f.friend.id);
              const alreadySeated = seatedIdSet.has(f.friend.id);
              const isBusy       = busyId === f.friend.id;
              const disabled     = alreadySent || alreadySeated || busyId !== null;

              let btnLabel = t('invite.invite');
              if (alreadySent)   btnLabel = t('invite.invited');
              if (alreadySeated) btnLabel = t('invite.atTable');
              if (isBusy)        btnLabel = '…';

              return (
                <div
                  key={f.friendship_id}
                  style={{
                    display:     'flex',
                    alignItems:  'center',
                    gap:         12,
                    padding:     '10px 20px',
                  }}
                >
                  <PresenceDot status={f.status} />

                  {/* Name — friends are always human, so the flag always applies. */}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <p style={{
                      margin: 0, display: 'flex', alignItems: 'center', gap: 6, fontSize: 14, fontWeight: 600,
                      fontFamily: DS2.font.body, color: INK,
                      overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                    }}>
                      <CountryFlag countryCode={f.friend.country_code} width={14} />
                      <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{f.friend.display_name}</span>
                    </p>
                    <p style={{
                      margin: 0, fontSize: 12, fontFamily: DS2.font.body, color: INK_MUTED,
                      overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                    }}>
                      {f.friend.email}
                    </p>
                  </div>

                  {/* Invite button */}
                  <button
                    onClick={() => handleInvite(f)}
                    disabled={disabled}
                    style={{
                      padding:         '6px 14px',
                      background:      alreadySent || alreadySeated
                        ? 'rgba(11,11,11,0.06)'
                        : DS2.gradient.gold,
                      color:           alreadySent || alreadySeated
                        ? INK_FAINT
                        : INK,
                      border:          'none',
                      borderRadius:    DT.radius.sm,
                      fontSize:        13,
                      fontWeight:      600,
                      fontFamily:      DS2.font.body,
                      cursor:          disabled ? 'default' : 'pointer',
                      opacity:         isBusy ? 0.6 : 1,
                      whiteSpace:      'nowrap',
                      flexShrink:      0,
                      transition:      `filter ${DT.motion.fast}ms`,
                      WebkitTapHighlightColor: 'transparent',
                    }}
                  >
                    {btnLabel}
                  </button>
                </div>
              );
            })
          )}
        </div>

        {/* Error footer */}
        {error && (
          <div style={{
            padding:     '10px 20px',
            borderTop:   '1px solid rgba(11,11,11,0.12)',
            flexShrink:  0,
          }}>
            <p style={{ margin: 0, fontSize: 12, fontFamily: DS2.font.body, color: DS2.modal.errorColor }}>
              {renderUiMessage(t, error)}
            </p>
          </div>
        )}
      </div>
    </DS2Modal>
  );
}
